import {
  CaretDownOutlined,
  CaretUpOutlined,
  FundViewOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import LineChart from "../../components/charts/apexLineChart";
import useEventsKPIData from "../../hooks/useEventsKpiData";
import useEventsTableData from "../../hooks/useEventsTableData";
import CollectionDetailed from "./CollectionDetailed";

const { Text, Title } = Typography;

export default function TableEventsWrapper({ selectedDate, zones, period }) {
  const [selectedRow, setSelectedRow] = useState({
    id: null,
    description: null,
  });

  const [eventType, setEventType] = useState("general_event");

  const { data: tableData, isLoading: isTableLoading } = useEventsTableData({
    month: dayjs(selectedDate).format("MM"),
    zone: zones,
    event_type: eventType,
  });

  const { data: kpiData, isLoading: isKpiLoading } = useEventsKPIData({
    id: selectedRow.id,
    enabled: selectedRow.id !== null,
    type: selectedRow.description,
  });

  const isCollectionEvent =
    kpiData?.kpi_data.description.event_type === "collection";

  const renderEventDetails = () => {
    const details = [
      {
        label: isCollectionEvent ? "Troca de coleção" : "Registro de evento",
        value: null,
        isTitle: true,
      },
      {
        label: isCollectionEvent ? "Coleção anterior: " : "Título:",
        value: isCollectionEvent
          ? kpiData?.kpi_data.description.previous
          : kpiData?.kpi_data.description.title,
      },
      {
        label: isCollectionEvent ? "Coleção posterior: " : "Comentário:",
        value: isCollectionEvent
          ? kpiData?.kpi_data.description.next
          : kpiData?.kpi_data.description.comment,
      },
      {
        label: isCollectionEvent ? "Data da troca: " : "Data do registro: ",
        value: dayjs(kpiData?.kpi_data.description.datetime).format(
          "DD/MM/YYYY HH:mm"
        ),
      },
    ];

    return details.map(({ label, value, isTitle }, index) => (
      <Space key={index} align="baseline">
        <Title level={isTitle ? 3 : 4} style={{ margin: 0 }}>
          {label}
        </Title>
        {!isTitle && <Text style={{ fontSize: 20 }}>{value}</Text>}
      </Space>
    ));
  };

  const handleClick = (record) => {
    setSelectedRow({ id: record.id, description: record.description });
  };

  const getTableColumns = useMemo(() => {
    const baseColumns = [
      {
        title: "Data e horário",
        dataIndex: "datetime",
        key: "datetime",
        width: "auto",
        ellipsis: {
          showTitle: false,
        },
        render: (address) => dayjs(address).format("DD/MM/YYYY HH:mm"),
        fixed: "left",
      },
      {
        title: "Zona",
        dataIndex: "zone",
        key: "zone",
        width: "auto",
        ellipsis: {
          showTitle: false,
        },
        render: (address) => (
          <Tooltip placement="topLeft" title={address}>
            {address}
          </Tooltip>
        ),
        fixed: "left",
      },
    ];

    // Dynamic columns based on event type
    const dynamicColumns =
      eventType === "collection_change"
        ? [
            {
              title: "Coleção Anterior",
              dataIndex: "previous_collection",
              key: "previous_collection",
              width: "auto",
              ellipsis: {
                showTitle: false,
              },
              render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                  {text || "N/A"}
                </Tooltip>
              ),
            },
            {
              title: "Coleção Nova",
              dataIndex: "next_collection",
              key: "next_collection",
              width: "auto",
              ellipsis: {
                showTitle: false,
              },
              render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                  {text || "N/A"}
                </Tooltip>
              ),
            },
          ]
        : [
            {
              title: "Título",
              dataIndex: "title",
              key: "title",
              width: "auto",
              ellipsis: {
                showTitle: false,
              },
              render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                  <Tag color="blue">{text}</Tag>
                </Tooltip>
              ),
            },
          ];

    const endColumns = [
      {
        title: "Tempo permanência",
        dataIndex: "permanence",
        key: "permanence",
        width: "auto",
        render: (value, record) =>
          value ? (
            <>
              <Tooltip placement="topLeft" title={value}>
                <Tag
                  color={record.permanence_color}
                  icon={
                    record.permanence_color === "green" ? (
                      <CaretUpOutlined />
                    ) : (
                      <CaretDownOutlined />
                    )
                  }>
                  {value + "%"}
                </Tag>
              </Tooltip>
            </>
          ) : (
            <Tag color="grey">Sem dados</Tag>
          ),
      },
      {
        title: "Faturamento",
        dataIndex: "revenue",
        key: "revenue",
        width: "auto",
        render: (value, record) =>
          value ? (
            <>
              <Tooltip placement="topLeft" title={value}>
                <Tag
                  color={record.revenue_color}
                  icon={
                    record.revenue_color === "green" ? (
                      <CaretUpOutlined />
                    ) : (
                      <CaretDownOutlined />
                    )
                  }>
                  {value + "%"}
                </Tag>
              </Tooltip>
            </>
          ) : (
            <Tag color="grey">Sem dados</Tag>
          ),
      },
      {
        title: "Analisar",
        dataIndex: "analyse",
        key: "analyse",
        width: "auto",
        render: (text, record) => (
          <Button
            icon={<FundViewOutlined />}
            onClick={() => {
              handleClick(record);
            }}
          />
        ),
      },
    ];

    return [...baseColumns, ...dynamicColumns, ...endColumns];
  }, [eventType]);

  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
            }}>
            <span
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}>
              Mudanças registradas
            </span>
            <div style={{ marginLeft: "auto" }}>
              <Select
                placeholder="Selecione o tipo de evento"
                style={{ width: 250 }}
                value={eventType}
                onChange={setEventType}
                options={[
                  { value: "general_event", label: "Eventos Gerais" },
                  { value: "collection_change", label: "Mudança de coleção" },
                ]}
              />
            </div>
          </div>
        }
        align="center"
        style={{ width: "85%" }}
        styles={{ header: { backgroundColor: "#d0d6e2" } }}
        loading={isTableLoading}>
        <Table
          columns={getTableColumns}
          dataSource={tableData}
          loading={isTableLoading}
          responsive="stack"
          align="center"
          pagination={{ pageSize: 10 }}
          scroll={{ y: 500, x: 300 }}
        />
      </Card>
      {selectedRow.id && (
        <>
          <Divider>
            <Title level={3}> Análise detalhada </Title>
          </Divider>
          <Card style={{ width: "85%" }} loading={isKpiLoading}>
            <Space direction="vertical" size="large" align="left">
              {renderEventDetails()}
            </Space>
          </Card>
          <Card
            title={
              kpiData?.kpi_data.description.event_type === "collection"
                ? `KPIs da troca de coleção - id: ${selectedRow.id}`
                : `KPIs do evento - id: ${selectedRow.id}`
            }
            align="center"
            style={{
              width: "85%",
            }}
            styles={{ header: { backgroundColor: "#d0d6e2" } }}
            loading={isKpiLoading}>
            {kpiData?.kpi_data?.occupation?.before_change ? (
              <CollectionDetailed kpiData={kpiData?.kpi_data} />
            ) : (
              <Empty />
            )}
          </Card>
          <Card
            title={`Faturamento da zona ${kpiData?.zone_description ?? ""}`}
            align="center"
            style={{ width: "85%" }}
            styles={{ header: { backgroundColor: "#d0d6e2" } }}
            loading={isKpiLoading}>
            {kpiData?.kpi_data?.revenue?.before_change != 0 ? (
              <LineChart
                title="Faturamento (R$)"
                data={kpiData?.revenue_graph_data}
                xAnnt={{
                  x: kpiData?.revenue_graph_data?.x_annotation,
                  text: "Evento",
                  strokeColor: "blue",
                }}
              />
            ) : (
              <Empty />
            )}
          </Card>
          <Card
            title={`Tempo de permanência da zona ${kpiData?.zone_description ?? ""}`}
            align="center"
            style={{ width: "85%" }}
            styles={{ header: { backgroundColor: "#d0d6e2" } }}
            loading={isKpiLoading}>
            {kpiData?.kpi_data?.occupation?.before_change ? (
              <LineChart
                title="Tempo de permanência (minutos)"
                data={kpiData?.graph_data}
                xAnnt={{
                  x: kpiData?.graph_data?.x_annotation,
                  text: "Evento",
                  strokeColor: "blue",
                }}
              />
            ) : (
              <Empty />
            )}
          </Card>
        </>
      )}
    </>
  );
}
